import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { listingsApi } from '@/api'
import { fetchPlacesByIds, placeName } from '@/components/place/placeApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { registerVirtualPlaceResolver } from '@/features/task-expense/virtualPlaceChoice'
import type { SnowflakeId } from '@/types/snowflake'

export interface VirtualPlaceChoice {
  id: SnowflakeId
  nickname: string
}

/** 取货人有多个虚拟地点时，选货进哪一个（PRD §8：多个必须选） */
export function VirtualPlaceChoiceDialog({
  open,
  places,
  onPick,
  onClose,
}: {
  open: boolean
  places: VirtualPlaceChoice[]
  onPick: (id: SnowflakeId) => void
  onClose: () => void
}) {
  const { t } = useTranslation('tasks')
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      {/* 高于助手浮窗里的 flyout：Host 挂在 Layout，收不到它们的 StackingLayer */}
      <DialogContent className="sm:max-w-sm" zIndexClass="z-[100]">
        <DialogHeader>
          <DialogTitle>{t('expense.virtualPlaceChoice.title')}</DialogTitle>
          <DialogDescription>{t('expense.virtualPlaceChoice.description')}</DialogDescription>
        </DialogHeader>
        {/* 长期挂的虚拟地点可能很多。外层 DialogContent 不滚动，超出的按钮点不到 */}
        <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto overflow-x-hidden p-1">
          {places.map((place) => (
            <Button key={String(place.id)} type="button" variant="outline" className="h-auto min-h-9 justify-start whitespace-normal text-left" onClick={() => onPick(place.id)}>
              {place.nickname}
            </Button>
          ))}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** 本人的接口不要 location 读码，先用它。缺的 id（代记取货时是收货人的）再按 id 取 */
async function loadChoices(listingIds?: string[]): Promise<VirtualPlaceChoice[]> {
  const mine = ((await listingsApi.getMyVirtualLocations()) ?? []) as unknown as VirtualPlaceChoice[]
  if (!listingIds?.length) return mine
  const wanted = new Set(listingIds)
  const known = mine.filter((place) => wanted.has(String(place.id)))
  const knownIds = new Set(known.map((place) => String(place.id)))
  const missing = listingIds.filter((id) => !knownIds.has(id))
  if (missing.length === 0) return known
  const others = await fetchPlacesByIds(missing)
  return [...known, ...others.map((row) => ({ id: row.id as unknown as SnowflakeId, nickname: placeName(row) }))]
}

/**
 * 挂在 Layout 上。后端回 VIRTUAL_LOCATION_REQUIRED 时 taskExpenseApi 经登记点叫它。
 * 候选来自错误的 `details.listing_ids`：代记取货时是收货人的地点。没有时取我的。
 * 几个请求同时撞这个错时：候选相同的共用一次选择，不同的排队，不丢任何一个调用方。
 */
export function VirtualPlaceChoiceHost() {
  const [places, setPlaces] = useState<VirtualPlaceChoice[] | null>(null)
  const settleRef = useRef<((id: SnowflakeId | null) => void) | null>(null)
  const inFlightRef = useRef<{ key: string; choice: Promise<SnowflakeId | null> } | null>(null)
  const queueRef = useRef<Promise<unknown>>(Promise.resolve())

  const settle = useCallback((id: SnowflakeId | null) => {
    const finish = settleRef.current
    settleRef.current = null
    setPlaces(null)
    finish?.(id)
  }, [])

  useEffect(() => {
    const askOnce = async (listingIds?: string[]) => {
      const choices = await loadChoices(listingIds)
      if (choices.length === 0) return null
      return new Promise<SnowflakeId | null>((resolve) => {
        settleRef.current = resolve
        setPlaces(choices)
      })
    }
    return registerVirtualPlaceResolver((listingIds) => {
      const key = [...(listingIds ?? [])].sort().join(',')
      if (inFlightRef.current?.key === key) return inFlightRef.current.choice
      const choice = queueRef.current.then(() => askOnce(listingIds))
      const entry = { key, choice }
      inFlightRef.current = entry
      queueRef.current = choice
        .catch(() => null)
        .finally(() => {
          if (inFlightRef.current === entry) inFlightRef.current = null
        })
      return choice
    })
  }, [])

  return (
    <VirtualPlaceChoiceDialog
      open={places !== null}
      places={places ?? []}
      onPick={settle}
      onClose={() => settle(null)}
    />
  )
}
